import React from 'react'
import Apps from '../../../api/apps'
import { PageHeading, Heading, Text, Link } from '../../shared/typography'
import { GridView, StackView, Alert } from '@planningcenter/tapestry-react'
import { NavLink } from 'react-router-dom'
import AppLink from '../app_link'

const page = () => {
  return (
    <StackView axis="vertical" spacing={2}>
      <PageHeading>Getting Started</PageHeading>

      <Text>
        Planning Center provides a REST API for developers to interact with a
        Planning Center account. Our API follows the{' '}
        <NavLink to="/overview/json-api">JSON-API</NavLink> spec.
      </Text>

      <Heading anchored id="user-account">User Account</Heading>

      <Text>
        All requests to our API are made on behalf of a user. As such, in order
        to make requests to our API you will need a{' '}
        <Text as="span" inline weight={600}>
          user account
        </Text>
        . The type of integration you are making will determine which type of
        user account you should use.
      </Text>
      <Text>
        If you are building an integration or tool for a single church, you can
        use a <strong>Personal Access Token</strong> on a user within that
        organization. If you’re building a third-party integration for a product
        or service, we suggest that you{' '}
        <Link to="https://accounts.planningcenteronline.com" external>
          create a new organization
        </Link>{' '}
        and create your OAuth Application on this account. You can access the
        full API on the free subscription tiers, and also add sample data for
        you to work with.
      </Text>

      <Text>
        From your user account, you can access your{' '}
        <Link
          to="https://api.planningcenteronline.com/oauth/applications"
          external
        >
          developer account
        </Link>
        . In your developer account, you can create new applications using
        OAuth, generate Personal Access Tokens, configure webhooks, and play
        with the API Explorer using real organizational data. You can read more
        about{' '}
        <NavLink to="/overview/authentication">API authentication</NavLink>.
      </Text>

      <Alert stroke="grey-6" backgroundColor="grey-4" marginBottom={2}>
        <Text size={4}>
          <strong>Important:</strong> Your integration will act under the{' '}
          <Link
            to="https://pcopeople.zendesk.com/hc/en-us/articles/204462570-Permissions"
            external
          >
            permission levels
          </Link>{' '}
          your user account has.
        </Text>
      </Alert>

      <Heading anchored id="staying-up-to-date">Staying Up-to-date</Heading>
      <Text>
        We try very hard not to introduce breaking changes to the API, but
        occasionally we need to make fundamental modifications that may require
        adjustments on your part. When that happens, we send an email to the API
        News mailing list, to which you can{' '}
        <Link to="http://eepurl.com/jempmE" target="_blank">sign up here</Link>.
      </Text>

      <Heading anchored id="product-specific-information">Product-Specific Information</Heading>
      <Text>
        A single API is used for all Planning Center products. However, the API
        itself is split into sections that correspond to each Planning Center
        product. Choose a product below to view its specific API documentation.
      </Text>

      <GridView
        columns="1fr"
        spacing={4}
        marginTop={2}
        mediaQueries={{
          md: {
            columns: '1fr 1fr',
          },
          lg: {
            columns: '1fr 1fr 1fr',
          },
          xl: {
            spacing: 8,
          },
        }}
      >
        {Apps.map(({ key, name, icon, background, beta }) => {
          return (
            <AppLink
              key={key}
              to={`/apps/${key}`}
              name={name}
              icon={icon}
              background={background}
              beta={beta}
            />
          )
        })}
      </GridView>
    </StackView>
  )
}

export default page
